import { useRef } from 'react';
import { Gift } from 'lucide-react';
import { Button } from 'app/javascript/components/ui/button';
import { useGrantFacialUploadBonus } from 'app/javascript/queries/useAdmin';

interface FacialUploadBonusControlsProps {
  slug: string;
  baseLimit: number | null;
  extraCredits: number;
}

export default function FacialUploadBonusControls({
  slug,
  baseLimit,
  extraCredits,
}: FacialUploadBonusControlsProps) {
  const grant = useGrantFacialUploadBonus(slug);
  const attemptKeyRef = useRef<string | null>(null);

  const grantBonus = () => {
    // A retry after an ambiguous response is still the same grant attempt.
    // Keep its key until the backend confirms success so the bonus cannot be
    // duplicated when the first response is lost after persistence.
    attemptKeyRef.current ??= crypto.randomUUID();
    grant.mutate(
      { idempotencyKey: attemptKeyRef.current },
      { onSuccess: () => (attemptKeyRef.current = null) },
    );
  };

  if (baseLimit === null) {
    return (
      <p className="text-xs text-muted-foreground">
        Este plano já oferece uploads faciais sem limite.
      </p>
    );
  }

  return (
    <div className="rounded-md border border-primary/20 bg-primary/5 p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Gift className="size-4 text-primary" />
            <p className="text-xs font-semibold uppercase tracking-[0.16em]">Bônus de uploads</p>
            <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary">
              +{extraCredits.toLocaleString('pt-BR')}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Adiciona 10 fotos ao limite e avisa o dono por e-mail e, se houver um dispositivo
            cadastrado, por push.
          </p>
        </div>

        <Button
          size="sm"
          disabled={grant.isPending}
          onClick={grantBonus}
        >
          {grant.isPending ? 'Concedendo…' : 'Conceder +10'}
        </Button>
      </div>

      {grant.isSuccess && grant.data.notification_status === 'scheduled' && (
        <p className="mt-2 text-xs text-emerald-400" role="status">
          Bônus concedido. Os avisos foram agendados nos canais disponíveis.
        </p>
      )}
      {grant.isSuccess && grant.data.notification_status === 'partial' && (
        <p className="mt-2 text-xs text-amber-400" role="alert">
          Bônus concedido. Um aviso foi agendado, mas outro canal falhou.
        </p>
      )}
      {grant.isSuccess && grant.data.notification_status === 'failed' && (
        <p className="mt-2 text-xs text-amber-400" role="alert">
          Bônus concedido, mas não foi possível confirmar o agendamento dos avisos.
        </p>
      )}
      {grant.isSuccess && grant.data.notification_status === 'replayed' && (
        <p className="mt-2 text-xs text-emerald-400" role="status">
          Este bônus já havia sido concedido. Nenhum crédito foi duplicado.
        </p>
      )}
      {grant.error && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {grant.error.message}
        </p>
      )}
    </div>
  );
}
